// OtherOperations.tsx
import { Box, Button, Flex, Text, Tooltip, useToast } from '@chakra-ui/react'
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { LuFileImage } from 'react-icons/lu'
import { IoImagesOutline } from 'react-icons/io5'

import { checkOperationPermission, PermissionsEnum } from '../../../../../utils'
import {
  allowExtensionOperations,
  getFileURL,
  isSignedURLRequired,
} from '../../../../../utils/mediaLibrary'
import { ACLPermissionEnum, MLSortParams } from '../../../../constants'
import type { FileDetailsResponse } from '../../../../interfaces/FileDetailsResponse'
import { MLContext, UserContext } from '../../../../providers'
import { MediaLibraryActions } from '../../../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../../../providers/PublicLinksInfoProvider'
import RemoveBgModal from '../RemoveBgModal'
import useGetFileUrlArguments from '../../../../hooks/useGetFileUrlArguments'
import PermissionGuard from '../../../PermissionGuard'
import { FeaturesEnum } from '../../../../../utils/features'

interface OtherOperationsProps {
  fileDetails: FileDetailsResponse
}

const OtherOperations: React.FC<OtherOperationsProps> = ({ fileDetails }) => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const [, dispatch] = useContext(MLContext)
  const toast = useToast()
  const { clientInfo } = useContext(PublicLinksInfoContext)

  const [isModalOpen, setModalOpen] = useState(false)
  const [imageURLToUse, setImageURLToUse] = useState<string>('')

  const getFileUrlDynamicArguments = useGetFileUrlArguments({
    clientInfo,
    user,
  })

  const { request } = useFetch<string>({ cachePolicy: CachePolicies.NO_CACHE })

  const getAndSetFileURL = useCallback(async () => {
    try {
      const { isPrivateFile, isPublished, url } = fileDetails
      if (
        isSignedURLRequired({
          isPrivateFile,
          isPublished,
          onlySignedGet: getFileUrlDynamicArguments.onlySignedGet,
          onlyNamedGet: getFileUrlDynamicArguments.onlyNamedGet,
        }) ||
        typeof getFileUrlDynamicArguments.firstCustomCname !== 'undefined'
      ) {
        const signed = await getFileURL({
          file: fileDetails,
          ...getFileUrlDynamicArguments,
          request,
          transformation: [],
        })
        setImageURLToUse(signed)
      } else {
        setImageURLToUse(url)
      }
    } catch {
      toast({
        description: t(
          'something_went_wrong',
          'Something went wrong. Please try again.',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }, [fileDetails, getFileUrlDynamicArguments, request, t, toast])

  useEffect(() => {
    void getAndSetFileURL()
  }, [getAndSetFileURL])

  const canShow = useMemo(() => {
    return (
      allowExtensionOperations(fileDetails) &&
      checkOperationPermission({
        user,
        aclPermission: [ACLPermissionEnum.CONTRIBUTE, ACLPermissionEnum.MANAGE],
        selectedFiles: [fileDetails],
        selectedFolders: [],
        selectedMediaCollections: [],
      })
    )
  }, [fileDetails, user])

  if (!canShow) return null

  return (
    <Fragment>
      <Box>
        <Text fontWeight="600" mb="12px">
          {t('other_operations', 'Other Operations')}
        </Text>

        <Flex gap="24px" flexWrap="wrap">
          <Tooltip
            label={t(
              'semantic_search_not_available',
              'Visual search is not available in your current plan. Please upgrade to access this feature.',
            )}
            isDisabled={user?.pricingPlan.featuresList.includes(
              FeaturesEnum.SEMANTIC_SEARCH,
            )}
            shouldWrapChildren
            hasArrow
          >
            <Button
              variant="link"
              size="sm"
              fontWeight="500"
              fontSize="14px"
              iconSpacing="4px"
              _hover={{ textDecoration: 'underline' }}
              leftIcon={<LuFileImage />}
              isDisabled={
                user?.pricingPlan.featuresList.includes(
                  FeaturesEnum.SEMANTIC_SEARCH,
                ) === false
              }
              onClick={async () => {
                let finalUrl = ''
                const data = await getFileURL({
                  file: fileDetails,
                  ...getFileUrlDynamicArguments,
                  request,
                })
                if (
                  data.includes('ik-s=') &&
                  data.includes('ik-auth-version')
                ) {
                  const url = new URL(data)
                  url.searchParams.delete('ik-auth-version')
                  url.searchParams.delete('ik-s')
                  url.searchParams.delete('ik-t')
                  finalUrl = url.toString()
                } else {
                  finalUrl = data
                }
                dispatch([
                  { type: MediaLibraryActions.RESET_SEARCH },
                  {
                    type: MediaLibraryActions.SET_MIN_SCORE,
                    payload: 0.8,
                  },
                  {
                    type: MediaLibraryActions.SET_Q,
                    payload: finalUrl,
                  },
                  {
                    type: MediaLibraryActions.SET_SEARCH_MODE,
                    payload: 'ai-image',
                  },
                  {
                    type: MediaLibraryActions.SET_SORT_PARAM,
                    payload: MLSortParams.RELEVANCE,
                  },
                  {
                    type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
                    payload: false,
                  },
                  {
                    type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                  },
                  {
                    type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
                  },
                ])
              }}
            >
              {t('find_similar', 'Find similar')}
            </Button>
          </Tooltip>

          <PermissionGuard
            permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
          >
            {(hasPermission: boolean) =>
              hasPermission ? (
                <Button
                  leftIcon={<IoImagesOutline />}
                  onClick={() => setModalOpen(true)}
                  variant="link"
                  iconSpacing="4px"
                  size="sm"
                  fontWeight="500"
                  fontSize="14px"
                  _hover={{ textDecoration: 'underline' }}
                >
                  {t('remove_background', 'Remove Background')}
                </Button>
              ) : (
                <></>
              )
            }
          </PermissionGuard>
        </Flex>
      </Box>

      <RemoveBgModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        fileDetails={fileDetails}
      />
    </Fragment>
  )
}

export default OtherOperations
